import React, { useMemo, useState } from 'react'
import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useTable } from 'packages/ui/Table'
import TableViewBase from 'packages/ui/Table/Table'
import { useNavigate, useParams } from 'react-router-dom'

import EmptyScreen from 'packages/ui/EmptyScreen'
import { IItem } from 'packages/ui/Table/models'
import { ResponsiveTableProvider, useResponsiveColumns } from 'packages/ui/Table'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'
import { useSidebar } from 'components/AuthenticatedLayout/Sidebar/SidebarContext'
import ScrollTable from 'components/global/CUK/ScrollTable'

import { useIPTV } from '../use-iptv'
import { IPTVChannel } from '../types'

import COLUMNS_CONFIG, { ExpandedRowDetails } from './config-columns'

interface TableProps {
  filters: {
    verbatim: string;
  }
}

const RESPONSIVE_COLUMNS_CONFIG = {
  mobile: ['logo_name', 'position', 'status', 'actions'],
  tablet: ['logo_name', 'position', 'status', 'type', 'quality', 'actions'],
  desktop: ['logo_name', 'position', 'status', 'ip_address', 'port', 'type', 'quality', 'actions'],
  desktopLarge: ['logo_name', 'position', 'status', 'ip_address', 'port', 'type', 'quality', 'actions']
}

const Table = ({ filters }: TableProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { id } = useParams()
  const { channels, loading, activeSiteId } = useIPTV(filters.verbatim)
  const [sortBy, setSortBy] = useState('position')
  const [sortDirection, setSortDirection] = useState('asc')
  const { expanded: sidebarExpanded } = useSidebar()

  const { visibleColumns, getVisibleColumns } = useResponsiveColumns(
    RESPONSIVE_COLUMNS_CONFIG,
    {
      sidebarExpanded,
      excludeColumns: id ? ['ip_address', 'port'] : []
    }
  )

  const columns = useMemo(() => {
    return COLUMNS_CONFIG.filter(({ name }) => visibleColumns.includes(name))
  }, [visibleColumns])

  const responsiveConfig = useMemo(() => ({
    expandedRowRenderer: {
      component: ExpandedRowDetails,
      shouldRender: (screenSize) => screenSize === 'mobile',
      getVisibleColumns
    }
  }), [getVisibleColumns])

  const sortedChannels = useMemo(() => {
    if (!channels?.length) return []
    
    return [...channels].sort((a, b) => {
      if (sortBy === 'status') {
        if (sortDirection === 'asc') {
          return a.has_errors === b.has_errors ? 0 : a.has_errors ? 1 : -1
        }
        return a.has_errors === b.has_errors ? 0 : a.has_errors ? -1 : 1
      }

      const aValue = a[sortBy]
      const bValue = b[sortBy]
      return sortDirection === 'asc' ? 
        aValue > bValue ? 1 : -1 :
        aValue < bValue ? 1 : -1
    })
  }, [channels, sortBy, sortDirection])

  const apiTable = useTable({
    rowKey: 'id',
    items: sortedChannels,
    loading,
    columns,
    onRowClick: (item: IItem) => {
      navigate({ pathname: `${(item as IPTVChannel).id}` })
    },
    responsiveConfig
  })

  if (!activeSiteId) {
    return <EmptyScreen icon="tv">{t('iptv.no_site_selected')}</EmptyScreen>
  }
  
  if (loading) {
    return <TableSkeleton />
  }  

  if (!channels?.length) {
    return <EmptyScreen icon="tv">{t('iptv.no_channels')}</EmptyScreen>
  }

  return (
    <ResponsiveTableProvider>
      <Stack
        sx={{
          overflow: 'hidden',
          overflowY: 'auto',
          flex: '1 1 auto',
          scrollbarWidth: 'none',
          height: '100%',
          '&::-webkit-scrollbar ': {
            display: 'none'
          }
        }}>
        <ScrollTable 
          collapse={Boolean(id)}
          shouldFetchNextPage={false}
          fetchNextPage={() => {}}
        >
          <TableViewBase
            apiTable={apiTable}
            sort={{
              by: sortBy,
              direction: sortDirection,
              onChange: (newSortBy) => {
                if (newSortBy === sortBy) {
                  setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
                } else {
                  setSortBy(newSortBy)
                  setSortDirection('asc')
                }
              }
            }}
          />
        </ScrollTable>
      </Stack>
    </ResponsiveTableProvider>
  )
}

export default Table
